import React, { useState, useEffect } from 'react';
import AdminLayout from '../../components/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Switch } from '../../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { rulesAPI, companiesAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { ClipboardList, Plus, Trash2, Loader2, HelpCircle } from 'lucide-react';
import { Toaster, toast } from 'sonner';

const RulesPage = () => {
  const { user } = useAuth();
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState('');
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    days_per_week: 2,
    team_must_coincide: true,
    priority_roles_guaranteed: []
  });
  const [priorityRole, setPriorityRole] = useState('');
  const [saving, setSaving] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);

  useEffect(() => {
    loadCompanies();
  }, []);

  useEffect(() => {
    if (selectedCompany) {
      loadRules();
    }
  }, [selectedCompany]);

  const loadCompanies = async () => {
    try {
      if (user?.role === 'superadmin') {
        const response = await companiesAPI.getAll();
        setCompanies(response.data);
        if (response.data.length > 0) {
          setSelectedCompany(response.data[0].id);
        }
      } else if (user?.company_id) {
        setSelectedCompany(user.company_id);
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadRules = async () => {
    try {
      const response = await rulesAPI.getAll(selectedCompany);
      setRules(response.data);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await rulesAPI.create({ ...formData, company_id: selectedCompany });
      toast.success('Regla creada exitosamente');
      setDialogOpen(false);
      setFormData({ name: '', description: '', days_per_week: 2, team_must_coincide: true, priority_roles_guaranteed: [] });
      loadRules();
    } catch (error) {
      toast.error('Error al crear regla');
    } finally {
      setSaving(false);
    }
  };

  const addPriorityRole = () => {
    if (priorityRole && !formData.priority_roles_guaranteed.includes(priorityRole)) {
      setFormData({
        ...formData,
        priority_roles_guaranteed: [...formData.priority_roles_guaranteed, priorityRole]
      });
      setPriorityRole('');
    }
  };

  const removePriorityRole = (role) => {
    setFormData({
      ...formData,
      priority_roles_guaranteed: formData.priority_roles_guaranteed.filter(r => r !== role)
    });
  };

  return (
    <AdminLayout>
      <Toaster position="top-right" />
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight" style={{ fontFamily: 'Cabinet Grotesk' }}>
              Reglas de Asignación
            </h1>
            <p className="text-slate-500 text-sm mt-1">
              Configura las reglas del motor de asignación
            </p>
          </div>
          <div className="flex gap-2">
            {user?.role === 'superadmin' && companies.length > 0 && (
              <Select value={selectedCompany} onValueChange={setSelectedCompany}>
                <SelectTrigger className="w-[180px] rounded-sm">
                  <SelectValue placeholder="Empresa" />
                </SelectTrigger>
                <SelectContent>
                  {companies.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            <Button onClick={() => setDialogOpen(true)} className="bg-[#002FA7] hover:bg-[#001B66] rounded-sm" data-testid="add-rule-btn">
              <Plus className="w-4 h-4 mr-2" />
              Nueva Regla
            </Button>
          </div>
        </div>

        {/* Enlace de ayuda */}
        <button
          onClick={() => setHelpOpen(true)}
          className="inline-flex items-center gap-1.5 text-sm text-[#002FA7] hover:underline cursor-pointer"
        >
          <HelpCircle className="w-4 h-4" />
          ¿Cómo funcionan las reglas?
        </button>

        {/* Modal de ayuda */}
        <Dialog open={helpOpen} onOpenChange={setHelpOpen}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Cabinet Grotesk' }}>¿Cómo funcionan las reglas?</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div className="flex items-center gap-3">
                <span className="badge-info shrink-0">Días/semana</span>
                <p className="text-sm text-slate-600">Cantidad de días que cada colaborador debe asistir presencialmente. El motor distribuye estos días de forma equitativa entre Lunes a Viernes.</p>
              </div>
              <div className="flex items-center gap-3">
                <span className="badge-success shrink-0">Coincidencia</span>
                <p className="text-sm text-slate-600">Si está activo, el motor intentará que los miembros de un mismo equipo coincidan al menos 1 día en oficina para facilitar la colaboración presencial.</p>
              </div>
              <div className="flex items-center gap-3">
                <span className="badge-info shrink-0">Roles prioritarios</span>
                <p className="text-sm text-slate-600">Los colaboradores con estos roles tienen <strong>prioridad garantizada</strong> para obtener sus días preferidos. Ejemplo: un "director_rrhh" siempre obtendrá los días que solicite antes que un analista.</p>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="w-8 h-8 animate-spin text-[#002FA7]" />
          </div>
        ) : rules.length === 0 ? (
          <Card className="border border-slate-200">
            <CardContent className="flex flex-col items-center justify-center h-64 text-center">
              <ClipboardList className="w-12 h-12 text-slate-300 mb-4" />
              <p className="text-slate-500">No hay reglas configuradas</p>
              <p className="text-sm text-slate-400 mt-1">Se usarán valores por defecto (2 días/semana)</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {rules.map((rule) => (
              <Card key={rule.id} className="border border-slate-200">
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <ClipboardList className="w-5 h-5 text-[#002FA7]" />
                    {rule.name}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {rule.description && <p className="text-sm text-slate-600">{rule.description}</p>}
                  <div className="flex flex-wrap gap-2">
                    <span className="badge-info">
                      {rule.days_per_week} días/semana
                    </span>
                    <span className={rule.team_must_coincide ? 'badge-success' : 'badge-warning'}>
                      {rule.team_must_coincide ? 'Equipos deben coincidir' : 'Sin coincidencia requerida'}
                    </span>
                  </div>
                  {rule.priority_roles_guaranteed?.length > 0 && (
                    <div>
                      <p className="text-xs text-slate-500 mb-1">Roles con prioridad:</p>
                      <div className="flex flex-wrap gap-1">
                        {rule.priority_roles_guaranteed.map((r) => (
                          <span key={r} className="badge-info">{r}</span>
                        ))}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Create Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Cabinet Grotesk' }}>Nueva Regla de Asignación</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label>Nombre de la regla</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  className="rounded-sm"
                  placeholder="Ej: Regla estándar"
                  data-testid="rule-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Descripción (opcional)</Label>
                <Input
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="rounded-sm"
                />
              </div>
              <div className="space-y-2">
                <Label>Días presenciales por semana</Label>
                <Input
                  type="number"
                  min="1"
                  max="5"
                  value={formData.days_per_week}
                  onChange={(e) => setFormData({ ...formData, days_per_week: parseInt(e.target.value) || 2 })}
                  className="rounded-sm"
                  data-testid="rule-days-input"
                />
              </div>
              <div className="flex items-center gap-3">
                <Switch
                  checked={formData.team_must_coincide}
                  onCheckedChange={(checked) => setFormData({ ...formData, team_must_coincide: checked })}
                />
                <Label>Equipos deben coincidir mínimo 1 día</Label>
              </div>
              <div className="space-y-2">
                <Label>Roles con prioridad garantizada</Label>
                <div className="flex gap-2">
                  <Input
                    value={priorityRole}
                    onChange={(e) => setPriorityRole(e.target.value)}
                    placeholder="Ej: gerente"
                    className="rounded-sm"
                  />
                  <Button type="button" onClick={addPriorityRole} variant="outline" className="rounded-sm">
                    Agregar
                  </Button>
                </div>
                <div className="flex flex-wrap gap-1 mt-2">
                  {formData.priority_roles_guaranteed.map((r) => (
                    <span key={r} className="badge-info cursor-pointer" onClick={() => removePriorityRole(r)}>
                      {r} ×
                    </span>
                  ))}
                </div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="rounded-sm">
                  Cancelar
                </Button>
                <Button type="submit" className="bg-[#002FA7] hover:bg-[#001B66] rounded-sm" disabled={saving} data-testid="save-rule-btn">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  Guardar
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};

export default RulesPage;
